import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Messages } from 'primereact/messages';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import Container from 'react-bootstrap/Container';
import adminApi from '../api/adminApi';
import membersApi from '../api/membersApi';
import MemberFormModal from './MemberFormModal';
import MembersPaginationControls from './MembersPaginationControls';
import MembersTable from './MembersTable';
import useMembers from '../hooks/useMembers';
import { useAuth } from '../contexts/AuthContext';

const Members = () => {
  const pageSizeOptions = [25, 50, 100];
  const navigate = useNavigate();
  const msgs = useRef(null);
  const toast = useRef(null);

  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [formLocations, setFormLocations] = useState([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const { accessContext, isAuthReady } = useAuth();

  const appProviderId = accessContext && accessContext.claims ? accessContext.claims.appProviderId : null;

  const { members, loading, error, pagination, loadMembers, saveMember, deleteMember } = useMembers({
    scope: 'internal',
    appProviderId,
  });

  const customerOptions = useMemo(
    () => [{ label: 'All Customers', value: null }, ...(customers || []).map((customer) => ({ label: customer.name, value: customer.id }))],
    [customers]
  );

  const locationOptions = useMemo(() => {
    const source = customerId === null
      ? locations
      : locations.filter((location) => Number(location.customerId) === Number(customerId));

    return [{ label: 'All Locations', value: null }, ...source.map((location) => ({ label: location.name, value: location.id }))];
  }, [locations, customerId]);

  const currentFormLocationOptions = useMemo(
    () => (formLocations || []).map((location) => ({ label: location.name, value: location.id })),
    [formLocations]
  );

  const currentPage = Number(pagination.page || page);
  const currentPageSize = Number(pagination.pageSize || pageSize);
  const totalCount = Number(pagination.totalCount || 0);
  const totalPages = Number(pagination.totalPages || 0);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        setLookupsLoading(true);
        const nextCustomers = await adminApi.getCustomersLookup();
        setCustomers(nextCustomers);
        const nextLocations = await membersApi.getInternalLocationLookup(nextCustomers.map((customer) => customer.id));
        setLocations(nextLocations);
      } catch (_) {
        if (toast.current) {
          toast.current.show({ severity: 'error', summary: '', detail: 'Failed to load member filters.' });
        }
      } finally {
        setLookupsLoading(false);
      }
    };

    loadLookups();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [customerId, locationId, search]);

  useEffect(() => {
    loadMembers({ customerId, locationId, search, page, pageSize }).catch(() => {});
  }, [customerId, locationId, search, page, pageSize, loadMembers]);

  const openCreate = async () => {
    setSelectedMember(null);
    if (customerId) {
      try {
        const nextLocations = await membersApi.getInternalLocationsByCustomer(customerId);
        setFormLocations(nextLocations);
      } catch (_) {
        setFormLocations([]);
      }
    } else {
      setFormLocations([]);
    }
    setModalVisible(true);
  };

  const openEdit = async (member) => {
    const memberCustomerId = Number(member.groupId);
    try {
      const detail = await membersApi.getSupportMemberById(memberCustomerId, member.id, { appProviderId });
      setSelectedMember(detail);
      const nextLocations = await membersApi.getInternalLocationsByCustomer(memberCustomerId);
      setFormLocations(nextLocations);
      setModalVisible(true);
    } catch (_) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: '', detail: 'Failed to load the selected member.' });
      }
    }
  };

  const handleDelete = async (member) => {
    const confirmed = window.confirm(`Delete member '${member.name || member.memberId || member.id}'?`);
    if (!confirmed) return;

    try {
      await deleteMember(member);
      const nextPage = currentPage > 0 && totalCount - 1 <= currentPage * currentPageSize
        ? currentPage - 1
        : currentPage;
      if (nextPage !== currentPage) {
        setPage(nextPage);
      } else {
        await loadMembers({ customerId, locationId, search, page: currentPage, pageSize: currentPageSize });
      }
      if (toast.current) {
        toast.current.show({ severity: 'success', summary: '', detail: 'Member deleted.' });
      }
    } catch (_) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: '', detail: 'Failed to delete member.' });
      }
    }
  };

  const handleSave = async (member) => {
    if (!member.groupId) {
      if (msgs.current) {
        msgs.current.show({ severity: 'warn', summary: '', detail: 'Customer/group is required.' });
      }
      return;
    }

    const isUpdate = Boolean(member && member.id);

    try {
      await saveMember(member);
      setModalVisible(false);
      setSelectedMember(null);
      if (isUpdate) {
        await loadMembers({ customerId, locationId, search, page: currentPage, pageSize: currentPageSize });
      } else {
        setPage(0);
      }
      if (toast.current) {
        toast.current.show({ severity: 'success', summary: '', detail: isUpdate ? 'Member updated.' : 'Member created.' });
      }
    } catch (_) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: '', detail: 'Failed to save member.' });
      }
    }
  };

  const handleModalGroupChange = async (nextCustomerId) => {
    if (!nextCustomerId) {
      setFormLocations([]);
      return;
    }

    try {
      const nextLocations = await membersApi.getInternalLocationsByCustomer(nextCustomerId);
      setFormLocations(nextLocations);
    } catch (_) {
      setFormLocations([]);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setCustomerId(null);
    setLocationId(null);
  };

  const onPageChange = (event) => {
    if (Number(event.rows) !== Number(pageSize)) {
      setPageSize(event.rows);
      setPage(0);
      return;
    }

    setPage(Math.floor(event.first / event.rows));
  };

  const progressbar = (
    <div className="card mt-3">
      <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
    </div>
  );

  const bootstrapping = !isAuthReady;

  return (
    <Container fluid="md" className="justify-content-md-center mt-5">
      <div className="panel">
        <div className="flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
          <div className="flex align-items-center gap-3 flex-wrap">
            <Button link icon="pi pi-arrow-left" onClick={() => navigate(-1)} className="p-0" />
            <h4 className="m-0">Members</h4>
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search members" />
            </span>
            <Dropdown
              value={customerId}
              options={customerOptions}
              onChange={(e) => {
                setCustomerId(e.value);
                setLocationId(null);
              }}
              placeholder="Filter by customer"
              loading={lookupsLoading}
              style={{ minWidth: '18rem' }}
            />
            <Dropdown
              value={locationId}
              options={locationOptions}
              onChange={(e) => setLocationId(e.value)}
              placeholder="Filter by location"
              loading={lookupsLoading}
              style={{ minWidth: '18rem' }}
            />
            <Button icon="pi pi-filter-slash" className="p-button-text" onClick={clearFilters} tooltip="Clear filters" tooltipOptions={{ position: 'bottom' }} />
            <Button icon="pi pi-refresh" className="p-button-text" onClick={() => loadMembers({ customerId, locationId, search, page, pageSize }).catch(() => {})} tooltip="Refresh members" tooltipOptions={{ position: 'bottom' }} />
          </div>
          <Button label="Create Member" icon="pi pi-plus" className="p-button-success" onClick={openCreate} />
        </div>
      </div>

      <Messages ref={msgs} />
      <Toast ref={toast} />
      {(bootstrapping || loading || lookupsLoading) && progressbar}
      {error && <p>{error}</p>}

      <MembersTable
        members={members}
        loading={loading}
        onEdit={openEdit}
        onDelete={handleDelete}
        emptyMessage={loading ? 'Loading members...' : 'No members found.'}
      />

      <MembersPaginationControls
        page={currentPage}
        pageSize={currentPageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        pageSizeOptions={pageSizeOptions}
        onPageChange={onPageChange}
      />

      <MemberFormModal
        visible={modalVisible}
        member={selectedMember}
        onHide={() => {
          setModalVisible(false);
          setSelectedMember(null);
        }}
        onSave={handleSave}
        saving={loading}
        customerOptions={(customers || []).map((customer) => ({ label: customer.name, value: customer.id }))}
        locationOptions={currentFormLocationOptions}
        defaultGroupId={customerId}
        lockGroupId={false}
        allowGroupEditOnExisting={false}
        onGroupChange={handleModalGroupChange}
      />
    </Container>
  );
};

export default Members;
